import { gql } from '@apollo/client'
import { CONTENT_BLOCK_PREVIEW_FRAGMENT } from '../ContentBlocks/contentBlockQueries'

export const GET_PRODUCT_DETAILS = gql`
  query GET_PRODUCT_DETAILS($id: Int!) {
    findUniqueProductContainer(where: { id: $id }) {
      id
      visible
      type
      reference
      primary_categoryId
      tags {
        id
        tag_profiles {
          name
          language
        }
      }
      categories {
        id
        category_profiles {
          name
          language
        }
      }
      collection {
        id
        collection_profiles {
          name
          language
        }
      }
      product_features {
        id
        display_order
        product_feature {
          id
          photo {
            id
            src_xs
            src
          }
          product_feature_profiles {
            id
            name
            description
            language
          }
        }
      }
      product_attributes {
        id
        product_attribute {
          id
          product_attribute_profiles {
            id
            name
            description
            language
          }
          product_attribute_group {
            id
            product_attribute_group_profiles {
              name
              language
            }
          }
        }
      }
      linked_products_as_source(orderBy: [{ link_type: asc }, { display_order: asc }]) {
        id
        link_type
        display_order
        target_product {
          id
          advanced_product {
            advanced_product_profiles {
              name
              language
            }
            images(take: 1, orderBy: { display_order: asc }) {
              src_thumbnail
            }
          }
          single_product {
            product_profiles {
              name
              language
            }
            images(take: 1) {
              src_thumbnail
            }
          }
        }
      }
      advanced_product {
        id
        category_photo_id
        advanced_product_type
        advanced_product_profiles {
          name
          language
          long_product_name
          original_product_name
          description
          meta_information {
            permalink
            meta_title
            meta_description
          }
        }

        package_dimensions {
          volume
          name
        }
        _count {
          sofa_forms
        }
        images {
          display_order
          id
          src_thumbnail
          src
          src_md
        }
        fabricCombinations {
          enabled
          visible
          metadata
          id
          code
          image {
            id
            src_md
            src
          }
          fabricCombinationOptions {
            id
            code
            image {
              id
              src_xs
              src
            }
            fabricCombinationOptionProfiles {
              id
              name
              description
              language
            }
          }
          fabricCombinationProfiles {
            id
            name
            description
            language
          }
        }
        additional_component_group_to_advanced_product {
          id
          enabled
          additional_component_group {
            code
            id
            use_fabric_prices_for_components
            additional_component_group_profiles {
              name
              language
            }
          }
        }
        additional_component_to_advanced_product {
          id
          advancedProductId
          additional_component {
            id
            additional_component_profiles {
              id
              name
              language
            }
            image {
              src_thumbnail
            }
            package_dimensions {
              id
              name
              volume
            }
            code
            component_sku
            additionalComponentGroupId
          }
          enabled
          metadata
          conditions
        }
        sofa_forms {
          id
          name
          code
        }
      }
      manufacturerId
      manufacturer {
        manufacturer_profiles {
          language
          name
        }
      }

      single_product {
        id
        category_photo_id
        sku
        product_profiles {
          language
          name
          description
        }
        images {
          id
          src
          src_md
          src_thumbnail
          display_order
        }
      }
    }
  }
`

export const DELETE_BULK_PRODUCTS = gql`
  mutation BULK_DELETE_PRODUCTS($ids: [Int!]) {
    bulkDeleteProductContainers(selected_products: $ids)
  }
`

export const GET_PRODUCT_TYPE = gql`
  query GetProductType($id: Int!) {
    findUniqueProductContainer(where: { id: $id }) {
      id
      type
    }
  }
`

export const PRODUCT_FILE_FIELDS = gql`
  fragment ProductFileFields on ProductFile {
    id
    s3_key
    url
    edition
    name
    display_order
    erp_managed
    suppressed
  }
`

export const GET_SIMPLE_PRODUCT_DETAILS = gql`
  ${CONTENT_BLOCK_PREVIEW_FRAGMENT}
  ${PRODUCT_FILE_FIELDS}
  query GetSimpleProductDetails($id: Int!) {
    findUniqueProductContainer(where: { id: $id }) {
      id
      type
      single_product {
        product_profiles {
          id
          name
          original_product_name
          description
          language
        }
      }
      content_blocks(orderBy: { arrangement: asc }) {
        ...ContentBlockPreviewFields
        arrangement
      }
      product_files(includeSuppressed: true) {
        ...ProductFileFields
      }
    }
  }
`

export const GET_ADVANCED_PRODUCT_DETAILS = gql`
  ${CONTENT_BLOCK_PREVIEW_FRAGMENT}
  ${PRODUCT_FILE_FIELDS}
  query GetAdvancedProductDetails($id: Int!) {
    findUniqueProductContainer(where: { id: $id }) {
      id
      type
      reference
      advanced_product {
        id

        advanced_product_profiles {
          id
          name
          long_product_name
          original_product_name
          description
          language
          meta_information {
            id
            permalink
            meta_title
            meta_description
          }
        }
      }
      content_blocks(orderBy: { arrangement: asc }) {
        ...ContentBlockPreviewFields
        arrangement
      }
      product_files(includeSuppressed: true) {
        ...ProductFileFields
      }
    }
  }
`

export const UPDATE_ADVANCED_PRODUCT_PROFILES = gql`
  mutation UpdateAdvancedProductProfiles(
    $id: Int!
    $advanced_product_profiles: [AdvancedProductProfileCustomInput!]!
    $reference: String
  ) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      advanced_product_profiles: $advanced_product_profiles
      reference: $reference
    ) {
      id
      reference
      advanced_product {
        advanced_product_profiles {
          id
          name
          long_product_name
          original_product_name
          description
          language
          meta_information {
            id
            permalink
            meta_title
            meta_description
          }
        }
      }
    }
  }
`

export const GET_SOFA_FORM_DETAILS = gql`
  query GetSofaFormDetails($id: Int!) {
    findUniqueSofaForm(where: { id: $id }) {
      id
      code
      enabled
      metadata
      left_and_right
      name
      type
      blueprint {
        status
        url
        thumbnail_url
        error
        hash
        width
        height
        thumb_width
        thumb_height
        clipped
        warnings
      }
      package_dimensions {
        id
        volume
        name
      }
      dimensions {
        height
        width
        length
        armrest_width
        backrest_width
        mattress_width
        mattress_length
        corner_part_length
        angle
        seat_height
        seat_depth
        seat_width
        extendable_part_length
        number_of_big_pillows
        number_of_small_pillows
        spread_of_big_pillows
        spread_of_small_pillows
        size_of_big_pillow
        size_of_pillow
        composition
        corner_radius
        seat_sections
        backrest_sections
        extension_type
        backrest_type
        covered_side
      }
    }
  }
`

export const DELETE_MANY_PRODUCTS = gql`
  mutation DeleteManyProducts($ids: [Int!]!) {
    deleteManyProductContainers(where: { id: { in: $ids } }) {
      count
    }
  }
`

export const UPDATE_PRODUCT_VISIBILITY = gql`
  mutation UpdateProductVisibility($id: Int!, $visible: Boolean!) {
    updateExistingAdvancedProduct2(where: { id: $id }, visible: $visible) {
      id
      visible
    }
  }
`

export const UPDATE_SIMPLE_PRODUCT_PROFILES = gql`
  mutation UpdateProductProfiles(
    $id: Int!
    $product_profiles: [ProductProfileUpdateInput!]!
  ) {
    updateOneProductContainer(
      where: { id: $id }
      data: {
        single_product: {
          update: { product_profiles: { updateMany: $product_profiles } }
        }
      }
    ) {
      id
      single_product {
        product_profiles {
          id
          name
          original_product_name
          description
          language
        }
      }
    }
  }
`

export const GET_PRODUCT_PRICING_DATA = gql`
  query GET_PRODUCT_PRICING_DATA($id: Int!) {
    findUniqueProductContainer(where: { id: $id }) {
      id
      advanced_product {
        id
        base_prices {
          id
          price
          price_from
          price_listId
          price_list {
            id
            name
          }
        }
        advanced_product_price_fabric_category {
          id
          price
          price_listId
          price_list {
            id
            name
          }
          fabrice_price_category {
            id
            group_number
          }
        }
        sofa_forms {
          id
          name
          code
          form_price_fabric_category {
            id
            price
            price_listId
            price_list {
              id
              name
            }
            fabrice_price_category {
              id
              group_number
            }
          }
        }
        additional_component_to_advanced_product {
          id
          advancedProductId
          additional_component {
            id
            code
            additional_component_profiles {
              id
              name
              language
            }
            additional_component_group {
              id
              use_fabric_prices_for_components
            }
          }
          extra_prices {
            id
            price
            price_listId
            price_list {
              id
              name
            }
          }
          price_fabric_category {
            id
            price
            price_listId
            price_list {
              id
              name
            }
            fabrice_price_category {
              id
              group_number
            }
          }
        }
      }
    }
  }
`

export const ADD_COMPONENT_GROUP_TO_PRODUCT = gql`
  mutation ADD_COMPONENT_GROUP_TO_PRODUCT(
    $advancedProductId: Int!
    $additionalComponentGroupId: Int!
    $enabled: Boolean = true
  ) {
    createOneAdditionalComponentGroupToAdvancedProduct(
      data: {
        enabled: $enabled
        advanced_product: { connect: { id: $advancedProductId } }
        additional_component_group: {
          connect: { id: $additionalComponentGroupId }
        }
      }
    ) {
      id
      enabled
      additional_component_group {
        id
        code
        use_fabric_prices_for_components
        additional_component_group_profiles {
          name
          language
        }
      }
    }
  }
`

export const UPDATE_PRODUCT_FEATURES = gql`
  mutation updateExistingAdvancedProduct2(
    $id: Int!
    $product_features: [ProductFeatureCustomInput]
  ) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      product_features: $product_features
    ) {
      id
      product_features {
        id
        display_order
        product_feature {
          id
          photo {
            id
            src_xs
            src
          }
          product_feature_profiles {
            id
            name
            description
            language
          }
        }
      }
    }
  }
`

export const UPDATE_PRODUCT_ATTRIBUTES = gql`
  mutation updateProductAttributes(
    $id: Int!
    $product_attributes: [ProductAttributeCustomInput]
  ) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      product_attributes: $product_attributes
    ) {
      id
      product_attributes {
        id
        product_attribute {
          id
          product_attribute_profiles {
            id
            name
            description
            language
          }
          product_attribute_group {
            id
            product_attribute_group_profiles {
              name
              language
            }
          }
        }
      }
    }
  }
`
